import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { clearTokens } from '../../api/tokenStorage';
import AuthShell from './AuthShell';
import { TextField, PasswordField, ErrorBanner } from './fields';
import { forgotPassword, resetPassword } from './authApi';
import { looksLikeEmail } from '../../utils/validators';

const RESEND_COOLDOWN = 60; // sn — "tekrar gönder" için basit cooldown
const MIN_PASSWORD = 6;

// Sunucudan gelen makine-okunur `code`'u çeviri metnine dönüştürür; tanınmayan
// kodda sunucunun İngilizce fallback metnine (veya genel metne) düşer.
const useCodeMessage = () => {
  const { t } = useTranslation();
  return useCallback(
    (code, fallback) =>
      code
        ? t(`reset.${code}`, { defaultValue: fallback || t('reset.generic') })
        : fallback || t('reset.generic'),
    [t]
  );
};

const ForgotPassword = () => {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const codeMessage = useCodeMessage();

  // 'email' → e-posta ekranı, 'reset' → kod + yeni şifre ekranı
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState(location.state?.email || '');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');

  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const timerRef = useRef(null);

  // "Tekrar gönder" geri sayımı
  useEffect(() => {
    if (seconds <= 0) return undefined;
    timerRef.current = setInterval(() => {
      setSeconds((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [seconds]);

  // Girişliyken şifre sıfırlamaya gerek yok.
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  // 1. adım — kod iste
  const handleRequestCode = async (e) => {
    e.preventDefault();
    if (loading) return;

    const cleanEmail = email.trim();
    if (!looksLikeEmail(cleanEmail)) {
      setError(t('auth.error.required'));
      return;
    }

    setError(null);
    setInfo(null);
    setLoading(true);
    try {
      await forgotPassword(cleanEmail);
      // Güvenlik notu: e-posta kayıtsız olsa bile 200 döner; "hesap var"
      // çıkarımı yapmadan kod ekranına geçiyoruz.
      setEmail(cleanEmail);
      setStep('reset');
      setSeconds(RESEND_COOLDOWN);
      setInfo(t('reset.RESET_CODE_SENT'));
    } catch (err) {
      // 429 RATE_LIMITED, 400 MAIL_SEND_FAILED — mail gitmedi, ekranda kal.
      setError(codeMessage(err.code, err.message));
    } finally {
      setLoading(false);
    }
  };

  // 2. adım — yeni şifreyi kaydet (kod aynı ekranda toplandığı için
  // verifyResetCode ön adımı atlanır, doğrudan resetPassword çağrılır)
  const handleReset = async (e) => {
    e.preventDefault();
    if (loading) return;

    if (code.trim().length !== 6) {
      setError(t('reset.INVALID_RESET_CODE'));
      return;
    }
    if (newPassword.length < MIN_PASSWORD) {
      setError(t('forgot.error.weakPassword'));
      return;
    }

    setError(null);
    setInfo(null);
    setLoading(true);
    try {
      await resetPassword({ email, code: code.trim(), newPassword });
      // Başarı: sunucu tüm oturumları geçersiz kıldı — yerel token'ları temizle
      // ve login'e yönlendir (e-posta önceden dolu + başarı mesajı).
      clearTokens();
      navigate('/login', {
        replace: true,
        state: { resetSuccess: true, email },
      });
    } catch (err) {
      // INVALID_RESET_CODE / EXPIRED_RESET_CODE / WEAK_PASSWORD
      setError(codeMessage(err.code, err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (seconds > 0 || loading) return;
    setError(null);
    setInfo(null);
    try {
      await forgotPassword(email);
      setSeconds(RESEND_COOLDOWN);
      setInfo(t('forgot.resent'));
    } catch (err) {
      setError(codeMessage(err.code, err.message));
    }
  };

  const backToEmail = () => {
    setStep('email');
    setCode('');
    setNewPassword('');
    setError(null);
    setInfo(null);
  };

  if (step === 'email') {
    return (
      <AuthShell
        eyebrow={t('forgot.eyebrow')}
        title={t('forgot.email.title')}
        subtitle={t('forgot.email.subtitle')}
        switchNode={<Link to="/login">{t('forgot.backToLogin')}</Link>}
      >
        <form className="auth-form" onSubmit={handleRequestCode} noValidate>
          <ErrorBanner message={error} />

          <TextField
            id="reset-email"
            label={t('auth.field.email')}
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder={t('auth.field.email.ph')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoFocus
          />

          <button type="submit" className="auth-submit" disabled={loading}>
            {loading && <span className="auth-spinner" />}
            {loading ? t('forgot.email.sending') : t('forgot.email.action')}
          </button>
        </form>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      eyebrow={t('forgot.eyebrow')}
      title={t('forgot.reset.title')}
      subtitle={t('forgot.reset.subtitle', { email })}
      switchNode={
        <button type="button" className="auth-linklike" onClick={backToEmail}>
          {t('forgot.changeEmail')}
        </button>
      }
    >
      <form className="auth-form" onSubmit={handleReset} noValidate>
        <ErrorBanner message={error} />
        {info && <div className="auth-info">{info}</div>}

        <TextField
          id="reset-code"
          label={t('forgot.field.code')}
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          placeholder={t('forgot.field.code.ph')}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
          className="code-input"
          autoFocus
        />

        <PasswordField
          id="reset-newPassword"
          label={t('forgot.field.newPassword')}
          autoComplete="new-password"
          placeholder={t('forgot.field.newPassword.ph')}
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
        />

        <button type="submit" className="auth-submit" disabled={loading}>
          {loading && <span className="auth-spinner" />}
          {loading ? t('forgot.reset.updating') : t('forgot.reset.action')}
        </button>

        <button
          type="button"
          className="auth-resend"
          onClick={handleResend}
          disabled={seconds > 0 || loading}
        >
          {seconds > 0
            ? t('forgot.resendIn', { seconds })
            : t('forgot.resend')}
        </button>

        <p className="auth-help">{t('forgot.help.noMail')}</p>
      </form>
    </AuthShell>
  );
};

export default ForgotPassword;
